import { isDevelopmentMode } from '@/config/config'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { CurveToCommand } from '@/lib/elements/types/curve-to-command'
import type { PathCommand } from '@/lib/elements/types/path-command'
import { pathCommandsToSvgPath } from '@/lib/elements/shape-path-commands'
import { anchorsToSvgPath, parseSvgPathToAnchors } from '@/lib/elements/pen-path-utils'
export function scalePathCommands(commands: PathCommand[], sx: number, sy: number): PathCommand[] {
  return commands.map((cmd) => {
    if ('moveTo' in cmd) {
      return { moveTo: { x: cmd.moveTo.x * sx, y: cmd.moveTo.y * sy } }
    }

    if ('lineTo' in cmd) {
      return { lineTo: { x: cmd.lineTo.x * sx, y: cmd.lineTo.y * sy } }
    }

    const ct = cmd.curveTo
    const out: CurveToCommand['curveTo'] = {
      controlX: ct.controlX * sx,
      controlY: ct.controlY * sy,
      x: ct.x * sx,
      y: ct.y * sy,
    }

    if (typeof ct.controlX2 === 'number' && typeof ct.controlY2 === 'number') {
      out.controlX2 = ct.controlX2 * sx
      out.controlY2 = ct.controlY2 * sy
    }

    if (typeof ct.deltaX === 'number' && typeof ct.deltaY === 'number') {
      out.deltaX = ct.deltaX * sx
      out.deltaY = ct.deltaY * sy
    }

    return { curveTo: out }
  })
}

/** Document shape lists must not contain duplicate ids; keeps first occurrence (stable z-order). */
export function dedupeShapesByIdPreserveOrder(
  shapes: ShapeData[],
  logWhenStripped?: string
): ShapeData[] {
  const seen = new Set<string>()
  let stripped = false
  const out: ShapeData[] = []

  for (const s of shapes) {
    if (seen.has(s.id)) {
      stripped = true
      continue
    }

    seen.add(s.id)
    out.push(s)
  }

  if (stripped && isDevelopmentMode() && logWhenStripped) {
    console.warn(`[shape-manager] ${logWhenStripped}: removed duplicate rows (first id wins).`)
  }

  return stripped ? out : shapes
}

/** Used by `updateShape` so `shapesRef` can be advanced before React runs `setShapes` updaters. */
export function applyPartialShapeUpdate(
  list: ShapeData[],
  shapeId: string,
  updates: Partial<ShapeData>
): ShapeData[] {
  return list.map((shape) => {
    if (shape.id !== shapeId) return shape

    const next: ShapeData = { ...shape, ...updates }

    const hasCommands = Array.isArray(shape.pathCommands) && shape.pathCommands.length > 0
    const sizeChanged = updates.width != null || updates.height != null

    if (hasCommands && sizeChanged && updates.path == null && updates.pathCommands == null) {
      const oldW = shape.width
      const oldH = shape.height
      const newW = next.width
      const newH = next.height

      if (
        oldW > 0 &&
        oldH > 0 &&
        newW > 0 &&
        newH > 0 &&
        Number.isFinite(oldW) &&
        Number.isFinite(oldH) &&
        Number.isFinite(newW) &&
        Number.isFinite(newH)
      ) {
        const sx = newW / oldW
        const sy = newH / oldH

        if (Number.isFinite(sx) && Number.isFinite(sy) && (sx !== 1 || sy !== 1)) {
          const scaled = scalePathCommands(shape.pathCommands as PathCommand[], sx, sy)

          next.pathCommands = scaled
          // For pen paths, preserve open/closed state from the stored path string.
          // Other shape types always close (default true). Without this, an open pen
          // path gains a spurious closing stroke on every resize.
          const close = shape.type !== 'path' || /Z\s*$/i.test((shape.path ?? '').trim())

          next.path = pathCommandsToSvgPath(scaled, { close })
        }
      }
    }

    const isPenPath = !hasCommands && typeof shape.path === 'string' && shape.path.trim().length > 0

    if (isPenPath && sizeChanged && updates.path == null) {
      const oldW = shape.width
      const oldH = shape.height
      const newW = next.width
      const newH = next.height

      if (
        oldW > 0 &&
        oldH > 0 &&
        newW > 0 &&
        newH > 0 &&
        Number.isFinite(oldW) &&
        Number.isFinite(oldH) &&
        Number.isFinite(newW) &&
        Number.isFinite(newH)
      ) {
        const sx = newW / oldW
        const sy = newH / oldH

        if (Number.isFinite(sx) && Number.isFinite(sy) && (sx !== 1 || sy !== 1)) {
          const { points, subpaths } = parseSvgPathToAnchors(shape.path!)
          // Derive the LAST subpath's closure from the explicit Z command only — the geometric
          // loop-back detection in parseSvgPathToAnchors can misfire when scaling shrinks the
          // path small enough that its first and last points fall within its EPSILON threshold.
          // Earlier subpaths in a compound path (e.g. a glyph outline's inner counter) are each
          // unambiguously terminated by their own real Z before the next M, so their parsed
          // closure is trusted as-is.
          const isClosed = /Z\s*$/i.test(shape.path!.trim())
          const scaledPoints = points.map((pt) => ({
            ...pt,
            x: pt.x * sx,
            y: pt.y * sy,
            handleIn: pt.handleIn ? { x: pt.handleIn.x * sx, y: pt.handleIn.y * sy } : null,
            handleOut: pt.handleOut ? { x: pt.handleOut.x * sx, y: pt.handleOut.y * sy } : null,
          }))
          const scaledSubpaths = subpaths.map((sp, idx) =>
            idx === subpaths.length - 1 ? { ...sp, isClosed } : sp
          )

          next.path = anchorsToSvgPath(scaledPoints, isClosed, scaledSubpaths)
        }
      }
    }

    return next
  })
}

export function getShapeById(shapes: ShapeData[], shapeId: string): ShapeData | undefined {
  return shapes.find((shape) => shape.id === shapeId)
}

export function computeShapePosition(
  x: number,
  y: number,
  width: number,
  height: number,
  anchor: 'center' | 'top-left' | 'bottom-right',
  anchorPoint?: { x: number; y: number }
): { left: number; top: number } {
  if (anchorPoint !== undefined) {
    return { left: x - anchorPoint.x, top: y - anchorPoint.y }
  }

  switch (anchor) {
    case 'top-left':
      return { left: x, top: y }
    case 'bottom-right':
      return { left: x - width, top: y - height }
    default:
      return { left: x - width / 2, top: y - height / 2 }
  }
}
